import { css } from "lit";
import { property } from "lit/decorators.js";
import { ContextData } from "@reporting/rp-types/analytics";
import { DimensionDefinition, ModuleDefinition } from "@reporting/rp-types/definition";
import { Layout, OmegaComponent, utils } from "@omegagrid/core";
import rp from '@reporting/rp-app-host';
import constants from "../../constants";
import { FilterSectionConfig } from "../../types";

export class FilterSectionEvent extends Event {
	public readonly context: ContextData;
	public readonly contextKey: string | keyof ContextData;
	public readonly sectionType: string;

	constructor(type: string, args: Partial<FilterSectionEvent>) {
		super(`filter.section.${type}`, {bubbles: true, composed: true});
		Object.assign(this, args);
	}
}

export class BaseSection extends OmegaComponent implements Layout {

	static styles = [OmegaComponent.styles,  css`
		:host {
			width: 100%;
			height: 100%;
			position: relative;
			overflow: hidden;
			padding: ${constants.FILTER_PADDING}px;
			padding-right: ${constants.FILTER_PADDING + 10}px;
		}

		.row {
			margin-bottom: ${constants.FILTER_PADDING}px;
		}
	`];

	@property({type: Object})
	module: ModuleDefinition;

	@property({type: Object})
	context: ContextData;

	@property({type: Array})
	fields: string[];

	config: FilterSectionConfig;

	get service() { return rp.ms('analytics') }

	private _dimensionMap: Map<string, DimensionDefinition>;
	get dimensionMap() {
		if (this._dimensionMap) return this._dimensionMap;
		this._dimensionMap = new Map();
		for (const dim of this.module.dimensions) {
			this._dimensionMap.set(dim.dim_num as string, dim);
			if (dim.properties) {
				for (const prop of dim.properties) {
					this._dimensionMap.set(`${dim.dim_num}:${prop.key}`, dim);
				}
			}
		}
		return this._dimensionMap;
	}

	get dimensions() { return this.module?.dimensions || [] }

	willUpdate(props: Map<PropertyKey, unknown>) {
		if (props.has('module')) {
			this._dimensionMap = null;
		}
	}

	_endPreviewTimeout: number;
	endPreview() {
		if (this._endPreviewTimeout) clearTimeout(this._endPreviewTimeout);
		this._endPreviewTimeout = window.setTimeout(() => {
			this.dispatchEvent(new FilterSectionEvent('endpreview', {context: this.context, sectionType: this.config?.type}));
			this._endPreviewTimeout = null;
		}, 1000);
	}

	previewContext(key: keyof ContextData, value: any) {
		if (this._endPreviewTimeout) clearTimeout(this._endPreviewTimeout);

		const context = utils.clone(this.context);
		const parts = key.split('.');
		parts.reduce((obj: Record<string, any>, part, index) => {
			if (index === parts.length - 1) {
				(obj as any)[part] = value;
			} else {
				if (!(obj as any)[part]) (obj as any)[part] = {};
			}
			return (obj as any)[part];
		}, context as Record<string, any>);

		this.dispatchEvent(new FilterSectionEvent('preview', {context, contextKey: key, sectionType: this.config?.type}));
	}

	updateContext(key: string, value: any) {
		const parts = key.split('.');
		parts.reduce((obj: Record<string, any>, part, index) => {
			if (index === parts.length - 1) {
				(obj as any)[part] = value;
			} else {
				if (!(obj as any)[part]) (obj as any)[part] = {};
			}
			return (obj as any)[part];
		}, this.context as Record<string, any>);

		// this.context[key] = value;
		this.dispatchEvent(new FilterSectionEvent('change', {
			context: this.context,
			contextKey: key,
			sectionType: this.config?.type
		}));
	}

	layout() { }

}
